<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);

const formatMessagesForApi = () => {
  // Bolhas de erro são só UI local — não entram no histórico enviado à API.
  return messages.value
    .filter(message => message.type !== 'error')
    .map(message => {
      const payload = {
        role: message.sender,
        content: message.content,
      };

      if (message.sender === 'assistant' && message.agentName) {
        payload.agent_name = message.agentName;
      }

      return payload;
    });
};

const resetConversation = () => {
  messages.value = [];
  newMessage.value = '';
};

// Watch for assistant ID changes and reset conversation
watch(
  () => assistantId,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      resetConversation();
    }
  }
);

const requestResponse = async content => {
  try {
    isLoading.value = true;
    const { data } = await CaptainAssistant.playground({
      assistantId,
      messageContent: content,
      messageHistory: formatMessagesForApi(),
    });

    // 'conversation_handoff' é o token interno de transferência — vira uma
    // bolha de estado em PT-BR (o content cru é mantido pro histórico da API).
    messages.value.push({
      content: data.response,
      type: data.response === 'conversation_handoff' ? 'handoff' : undefined,
      sender: 'assistant',
      agentName: data.agent_name,
      toolCalls: data.tool_calls || [],
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    messages.value.push({
      type: 'error',
      sender: 'assistant',
      content:
        'Não foi possível obter a resposta da IA. Verifique a conexão e tente novamente.',
      retryContent: content,
      timestamp: new Date().toISOString(),
    });
  } finally {
    isLoading.value = false;
  }
};

const sendMessage = async () => {
  if (!newMessage.value.trim() || isLoading.value) return;

  const userMessage = {
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date().toISOString(),
  };
  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';

  await requestResponse(currentMessage);
};

const handleRetry = async index => {
  if (isLoading.value) return;
  const failed = messages.value[index];
  if (!failed || failed.type !== 'error') return;

  // Remove a bolha de erro e refaz a chamada com a mesma mensagem (que já
  // está no histórico — o sendMessage a empilhou antes da falha).
  messages.value.splice(index, 1);
  await requestResponse(failed.retryContent);
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendMessage();
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-2xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-extrabold">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          sm
          slate
          icon="i-ri-anticlockwise-line"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList
      :messages="messages"
      :is-loading="isLoading"
      @retry="handleRetry"
    />

    <div
      class="flex items-center mx-6 bg-n-background outline outline-1 outline-n-weak rounded-2xl p-3"
    >
      <input
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        @keydown.enter.exact="handleEnterKey"
      />
      <NextButton
        ghost
        sm
        :disabled="!newMessage.trim()"
        icon="i-ri-send-plane-line"
        @click="sendMessage"
      />
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('CAPTAIN.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
